import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Highlight, themes } from "prism-react-renderer";
import {
  ApiError,
  isProRequiredError,
  deleteApp,
  deleteAppTool,
  getApp,
  getAppTool,
  deleteSecret,
  listAppTools,
  listApps,
  listMembers,
  listSecrets,
  listToolVersions,
  publishProject,
  restoreToolVersion,
  saveToolToApp,
  setFetchAllowlist,
  setSecret,
  unpublishProject,
  updateWidgetFooter,
  updateAppTool,
  type App,
  type AppTool,
  type AppToolDetail,
} from "../lib/api";
import { downloadProjectZip, downloadWidgetZip, formatTsx } from "../lib/widgetActions";
import { ConnectCard } from "../components/ConnectCard";
import { ProUpgradeNudge } from "../components/ProUpgradeNudge";
import { UsagePane } from "../components/UsagePane";
import { MBChip, MBDot, MBIcon } from "../components/mb";

type Pane = "preview" | "source" | "data" | "versions" | "usage" | "triggers";

export function AppDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  // `?as=<shareId>` lets an invitee view the OWNER's project through their
  // share row. Without it every project-scoped fetch would resolve to the
  // invitee's own empty namespace and 404. Threaded into every query key +
  // every API call so the SPA mirrors the REST `?as=` behavior.
  const shareId = searchParams.get("as");
  const nav = useNavigate();
  const qc = useQueryClient();
  const isInboxProject = slug === "__inbox";
  // Inbox defaults to gallery mode (selectedTool = null → show grid).
  // Other projects open straight to a detail view of the first tool.
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [pane, setPane] = useState<Pane>("preview");
  const [saveOpen, setSaveOpen] = useState(false);

  const app = useQuery({
    queryKey: ["app", slug, shareId],
    queryFn: () => getApp(slug!, shareId),
    enabled: !!slug,
    retry: false,
  });
  const tools = useQuery({
    queryKey: ["app-tools", slug, shareId],
    queryFn: () => listAppTools(slug!, shareId),
    enabled: !!slug,
    retry: false,
  });
  const detail = useQuery({
    queryKey: ["app-tool", slug, selectedTool, shareId],
    queryFn: () => getAppTool(slug!, selectedTool!, shareId),
    enabled: !!slug && !!selectedTool,
    retry: false,
  });

  const sortedTools = useMemo(() => {
    if (!tools.data) return [];
    return [...tools.data].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""));
  }, [tools.data]);

  const [zipping, setZipping] = useState(false);

  const removeTool = useMutation({
    mutationFn: (toolSlug: string) => deleteAppTool(slug!, toolSlug, shareId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["app-tools", slug, shareId] });
      setSelectedTool(null);
      setPane("preview");
    },
  });

  const toggleExpose = useMutation({
    mutationFn: (v: { toolSlug: string; exposed: boolean }) =>
      updateAppTool(slug!, v.toolSlug, { exposed: v.exposed }, shareId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["app-tools", slug, shareId] }),
  });

  const removeProject = useMutation({
    mutationFn: () => deleteApp(slug!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["apps"] });
      nav("/projects");
    },
  });

  // Download every renderable widget in this project as one .zip. Fetches
  // each widget's source/data in parallel (the list endpoint omits them),
  // skips any without stored source (pre-2026-05-21 widgets), then zips
  // one folder per widget.
  async function handleDownloadAll() {
    if (!slug) return;
    const list = sortedTools.filter((t) => t.renderable !== false);
    if (list.length === 0) return;
    setZipping(true);
    try {
      const details = await Promise.all(
        list.map((t) => getAppTool(slug, t.toolSlug, shareId).catch(() => null)),
      );
      const widgets = details
        .filter((d): d is NonNullable<typeof d> => !!d && !!d.source)
        .map((d) => ({
          toolSlug: d.toolSlug,
          description: d.description,
          source: d.source as string,
          data: d.data,
        }));
      if (widgets.length === 0) {
        window.alert("No widgets with stored source to download.");
        return;
      }
      await downloadProjectZip({ appSlug: slug, appName: app.data?.name, widgets });
    } finally {
      setZipping(false);
    }
  }

  // On Inbox: selectedTool=null → gallery, set value → detail. Other
  // projects skip the gallery and open to the first tool by default.
  const inferredSelected =
    selectedTool === null && isInboxProject
      ? undefined
      : sortedTools.find((t) => t.toolSlug === selectedTool) ?? sortedTools[0];
  const selected = inferredSelected;
  const selectedSlug = selected?.toolSlug;
  const isDataTool = selected?.type === "data";
  // Data tools have no widget / source / version history, so route those panes
  // back to the (data-tool-aware) preview rather than showing empty shells.
  const effectivePane: Pane =
    isDataTool && (pane === "source" || pane === "versions") ? "preview" : pane;

  const error = app.error || tools.error;
  if (error instanceof ApiError && error.status === 401) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10 text-fg-1 text-sm">Sign in first.</div>
    );
  }

  const isInbox = isInboxProject;
  const showGallery = isInbox && !selectedTool;

  if (showGallery) {
    const renderable = sortedTools.filter((t) => t.renderable !== false);
    const hidden = sortedTools.length - renderable.length;
    return (
      <InboxGallery
        appName={app.data?.name ?? "Inbox"}
        appIcon={app.data?.icon ?? "📥"}
        tools={renderable}
        hiddenCount={hidden}
        loading={tools.isLoading}
        zipping={zipping}
        onDownloadAll={handleDownloadAll}
        onPick={(toolSlug) => {
          setSelectedTool(toolSlug);
          setPane("preview");
        }}
      />
    );
  }

  return (
    <div className="h-[calc(100vh-3.5rem)] grid grid-cols-[320px_1fr]">
      <aside className="border-r border-bg-2 p-5 overflow-y-auto">
        <div className="mb-5">
          <Link
            to="/projects"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              letterSpacing: "0.04em",
              color: "var(--fg-3)",
              textTransform: "lowercase",
              marginBottom: 14,
            }}
          >
            <MBIcon name="chevron" size={11} style={{ transform: "rotate(180deg)" }} /> all projects
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: "var(--surface-2)",
                border: "1px solid var(--line-2)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
              }}
            >
              {app.data?.icon ?? (isInbox ? "📥" : "🧩")}
            </span>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontFamily: "var(--font-sans)", fontSize: 20, fontWeight: 600, color: "var(--fg-1)", letterSpacing: "-0.02em" }}>
                {app.data?.name ?? slug}
              </h1>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-3)" }}>{slug}</div>
            </div>
          </div>
          {isInbox && (
            <div className="mt-3 text-xs text-fg-2 leading-relaxed">
              Ephemeral widgets from <code className="font-mono">dynamic_widget</code>.
              Each entry expires 24h after creation — save anything you want to keep.
            </div>
          )}
        </div>
        {!isInbox && slug && (
          <div className="mb-4 space-y-3">
            <ConnectCard
              url={`${window.location.origin}/mcp/${slug}`}
              title="Connect this project"
              subtitle="Add this endpoint in Claude to call this project's tools directly. Only tools toggled on (live) appear."
            />
            <SharePanel
              slug={slug}
              isPublic={app.data?.public === true}
              shareToken={app.data?.shareToken ?? null}
            />
            <TeamPanel slug={slug} />
            <LiveDataPanel slug={slug} allowlist={app.data?.fetchAllowlist ?? []} />
            <BrandingPanel
              slug={slug}
              footerText={app.data?.footerText ?? null}
              footerHidden={app.data?.footerHidden ?? false}
            />
          </div>
        )}
        <div className="text-xs uppercase tracking-wider text-fg-2 mb-2">
          {isInbox ? "Recent widgets" : "Tools"}
        </div>
        {tools.isLoading && <div className="text-fg-1 text-xs">Loading…</div>}
        {tools.data && tools.data.length === 0 && (
          <div className="text-fg-1 text-xs">
            {isInbox
              ? "No widgets yet. Use dynamic_widget from Claude or ChatGPT."
              : "No saved tools yet. Call save_widget_to_app from Claude."}
          </div>
        )}
        {sortedTools.length > 0 && (
          <ul className="space-y-1">
            {sortedTools.map((t) => (
              <li key={t.toolSlug} className="flex items-stretch gap-1">
                <button
                  onClick={() => setSelectedTool(t.toolSlug)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: "left",
                    padding: "10px 12px",
                    borderRadius: 6,
                    background: selectedSlug === t.toolSlug ? "var(--surface-2)" : "var(--surface-1)",
                    border: `1px solid ${selectedSlug === t.toolSlug ? "var(--accent-line)" : "var(--line-2)"}`,
                    boxShadow:
                      selectedSlug === t.toolSlug
                        ? "inset 3px 0 0 var(--accent)"
                        : "none",
                    cursor: "pointer",
                    color: "var(--fg-1)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div className="font-mono text-xs truncate flex items-center gap-1.5" style={{ color: "var(--fg-1)", fontWeight: 500 }}>
                    {t.type === "data" && (
                      <span className="shrink-0 px-1 rounded bg-accent/15 text-accent text-[9px] uppercase tracking-wide">
                        data
                      </span>
                    )}
                    <span className="truncate">{t.toolSlug}</span>
                  </div>
                  {t.description && (
                    <div style={{ fontFamily: "var(--font-sans)", fontSize: 12.5, lineHeight: "18px", color: "var(--fg-3)" }} className="truncate">
                      {t.description}
                    </div>
                  )}
                  {t.updatedAt && (
                    <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--fg-4)" }}>
                      {new Date(t.updatedAt).toLocaleString()}
                    </div>
                  )}
                </button>
                {!isInbox && (
                  <button
                    title={
                      t.exposed
                        ? "Live on the project endpoint — click to hide"
                        : "Hidden from the project endpoint — click to expose"
                    }
                    disabled={toggleExpose.isPending}
                    onClick={() =>
                      toggleExpose.mutate({ toolSlug: t.toolSlug, exposed: !t.exposed })
                    }
                    style={{
                      flexShrink: 0,
                      alignSelf: "flex-start",
                      marginTop: 1,
                      padding: "3px 9px",
                      borderRadius: 4,
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      letterSpacing: "0.04em",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                      background: t.exposed ? "var(--success-soft)" : "var(--surface-2)",
                      color: t.exposed ? "var(--success)" : "var(--fg-3)",
                      border: `1px solid ${t.exposed ? "rgba(61,207,142,0.32)" : "var(--line-2)"}`,
                      opacity: toggleExpose.isPending ? 0.5 : 1,
                    }}
                  >
                    {t.exposed && <MBDot color="var(--success)" />}
                    {t.exposed ? "live" : "hidden"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {sortedTools.length > 0 && (
          <div className="mt-4 pt-3 border-t border-bg-2 space-y-1.5">
            <button
              onClick={handleDownloadAll}
              disabled={zipping}
              className="w-full text-left px-2 py-1.5 rounded text-xs text-fg-1 hover:text-fg-0 hover:bg-bg-1 disabled:opacity-50"
            >
              {zipping ? "Zipping…" : "⬇ Download all (.zip)"}
            </button>
            {!isInbox && (
              <button
                disabled={removeProject.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `Delete project "${app.data?.name ?? slug}" and all ${sortedTools.length} of its widgets? This can't be undone.`,
                    )
                  ) {
                    removeProject.mutate();
                  }
                }}
                className="w-full text-left px-2 py-1.5 rounded text-xs text-red-400 hover:bg-red-500/10 disabled:opacity-50"
              >
                {removeProject.isPending ? "Deleting…" : "🗑 Delete project"}
              </button>
            )}
          </div>
        )}
      </aside>

      <section className="p-4 flex flex-col gap-3 overflow-hidden">
        <div className="flex items-center gap-2 flex-wrap">
          {isInbox && (
            <button
              onClick={() => setSelectedTool(null)}
              className="px-2 py-1 rounded text-xs text-fg-1 hover:text-fg-0 hover:bg-bg-2"
            >
              ← Gallery
            </button>
          )}
          <div className="mr-auto flex items-center gap-2 flex-wrap">
            {selected ? (
              <>
                <MBDot color="var(--accent)" />
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", color: "var(--fg-4)", textTransform: "uppercase" }}>
                  Editing
                </span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 12.5, letterSpacing: "0.04em", color: "var(--fg-1)", fontWeight: 500 }}>
                  {selected.toolSlug}
                </span>
              </>
            ) : (
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.06em", color: "var(--fg-3)", textTransform: "uppercase" }}>
                No tool selected
              </span>
            )}
            {selected && isDataTool && <MBChip accent>data</MBChip>}
            {selected && !isInbox &&
              (selected.exposed ? (
                <MBChip success>
                  <MBDot color="var(--success)" /> live
                </MBChip>
              ) : (
                <MBChip>hidden</MBChip>
              ))}
          </div>
          {isInbox && selected && (
            <button
              onClick={() => setSaveOpen(true)}
              className="px-2 py-1 rounded text-xs bg-accent text-white hover:opacity-90"
            >
              Save to project
            </button>
          )}
          {selected && detail.data?.source && (
            <button
              onClick={() =>
                downloadWidgetZip({
                  appSlug: slug ?? "",
                  toolSlug: selected.toolSlug,
                  description: detail.data?.description ?? null,
                  source: detail.data?.source ?? "",
                  data: detail.data?.data ?? {},
                })
              }
              className="px-2 py-1 rounded text-xs border border-bg-2 text-fg-1 hover:text-fg-0"
            >
              Download .zip
            </button>
          )}
          {selected && (
            <button
              disabled={removeTool.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Remove "${selected.toolSlug}" from ${app.data?.name ?? slug}? This permanently deletes the widget and its files.`,
                  )
                ) {
                  removeTool.mutate(selected.toolSlug);
                }
              }}
              className="px-2 py-1 rounded text-xs border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
            >
              {removeTool.isPending ? "Removing…" : "Remove"}
            </button>
          )}
          <PaneTab label={isDataTool ? "Tool" : "Preview"} active={effectivePane === "preview"} onClick={() => setPane("preview")} />
          {!isDataTool && (
            <PaneTab label="Source" active={effectivePane === "source"} onClick={() => setPane("source")} />
          )}
          <PaneTab label="Data" active={effectivePane === "data"} onClick={() => setPane("data")} />
          {!isDataTool && (
            <PaneTab label="Versions" active={effectivePane === "versions"} onClick={() => setPane("versions")} />
          )}
          {!isInbox && (
            <PaneTab label="Triggers" active={effectivePane === "triggers"} onClick={() => setPane("triggers")} />
          )}
          {!isInbox && (
            <PaneTab label="Usage" active={effectivePane === "usage"} onClick={() => setPane("usage")} />
          )}
          {selected?.htmlUrl && (
            <a
              href={selected.htmlUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-fg-2 hover:text-fg-0 font-mono"
            >
              raw ↗
            </a>
          )}
        </div>

        {effectivePane === "preview" && (
          isDataTool ? (
            <DataToolView detail={detail.data} loading={detail.isLoading} />
          ) : selected?.htmlUrl ? (
            <div
              className="flex-1 min-h-0 flex flex-col overflow-hidden"
              style={{
                border: "1px solid var(--line-2)",
                borderRadius: 12,
                background: "var(--surface-1)",
                boxShadow: "0 1px 0 rgba(255,255,255,0.04) inset, 0 10px 28px rgba(0,0,0,0.45)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: "1px solid var(--line-1)" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--surface-4)" }} />
                  ))}
                </div>
                <span style={{ marginLeft: 8, fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--fg-4)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  mcpbuild.app/{isInbox ? "inbox" : `p/${slug}`}/{selected.toolSlug}
                </span>
                <a href={selected.htmlUrl} target="_blank" rel="noreferrer" style={{ marginLeft: "auto", color: "var(--fg-4)", display: "inline-flex" }} title="Open raw">
                  <MBIcon name="external" size={13} />
                </a>
              </div>
              <WidgetPreviewIframe
                appSlug={slug ?? ""}
                shareId={shareId}
                tool={selected}
                toolData={detail.data?.data}
              />
            </div>
          ) : (
            <div className="flex-1 rounded-md bg-bg-1 border border-bg-2 grid place-items-center text-fg-2 text-sm">
              {sortedTools.length === 0 ? "Nothing to preview yet." : "Pick a tool on the left."}
            </div>
          )
        )}

        {effectivePane === "source" && (
          <CodePane
            text={detail.data?.source ?? null}
            loading={detail.isLoading}
            filename={`${selected?.toolSlug ?? "widget"}.tsx`}
            mime="text/plain"
            language="tsx"
            canFormat
            emptyReason={
              !selected
                ? "no-selection"
                : isDataTool
                ? "data-tool"
                : detail.isLoading
                ? "loading-tool"
                : "pre-storage"
            }
          />
        )}

        {effectivePane === "data" && (
          <CodePane
            text={detail.data?.data != null ? JSON.stringify(detail.data.data, null, 2) : null}
            loading={detail.isLoading}
            filename={`${selected?.toolSlug ?? "widget"}.data.json`}
            mime="application/json"
            language="json"
            emptyReason={
              !selected
                ? "no-selection"
                : detail.isLoading
                ? "loading-tool"
                : "pre-storage"
            }
          />
        )}

        {effectivePane === "versions" &&
          (selectedSlug ? (
            <VersionsPane appSlug={slug ?? ""} toolSlug={selectedSlug} />
          ) : (
            <div className="flex-1 rounded-md bg-bg-1 border border-bg-2 grid place-items-center text-fg-2 text-sm">
              Pick a tool to see its versions.
            </div>
          ))}

        {effectivePane === "triggers" &&
          (selected && !isInbox ? (
            <TriggerPhrasesPane
              appSlug={slug ?? ""}
              toolSlug={selected.toolSlug}
              initialPhrases={selected.triggerPhrases ?? []}
              shareId={shareId}
              onSaved={() => qc.invalidateQueries({ queryKey: ["app-tools", slug, shareId] })}
            />
          ) : (
            <div className="flex-1 rounded-md bg-bg-1 border border-bg-2 grid place-items-center text-fg-2 text-sm">
              {isInbox
                ? "Triggers aren't tracked for the Inbox."
                : "Pick a tool to set its trigger phrases."}
            </div>
          ))}

        {effectivePane === "usage" &&
          (isInbox ? (
            <div className="flex-1 rounded-md bg-bg-1 border border-bg-2 grid place-items-center text-fg-2 text-sm">
              Usage analytics aren't tracked for the Inbox.
            </div>
          ) : (
            <UsagePane slug={slug ?? ""} />
          ))}
      </section>

      {saveOpen && selected && (
        <SaveToAppModal
          sourceAppSlug={slug ?? ""}
          sourceToolSlug={selected.toolSlug}
          defaultDescription={detail.data?.description ?? ""}
          onClose={() => setSaveOpen(false)}
          onSaved={(targetAppSlug, targetToolSlug) => {
            setSaveOpen(false);
            qc.invalidateQueries({ queryKey: ["apps"] });
            qc.invalidateQueries({ queryKey: ["app-tools", targetAppSlug] });
            nav(`/projects/${targetAppSlug}`);
            setTimeout(() => setSelectedTool(targetToolSlug), 0);
          }}
        />
      )}
    </div>
  );
}

function BrandingPanel({
  slug,
  footerText,
  footerHidden,
}: {
  slug: string;
  footerText: string | null;
  footerHidden: boolean;
}) {
  const qc = useQueryClient();
  const DEFAULT_FOOTER = "Powered by MCPBuild";
  const [text, setText] = useState(footerText ?? "");
  const [hidden, setHidden] = useState(footerHidden);
  const save = useMutation({
    mutationFn: () => updateWidgetFooter(slug, { footerText: text, footerHidden: hidden }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["app", slug] }),
  });
  const preview = hidden ? "(no footer)" : text.trim() || DEFAULT_FOOTER;
  return (
    <div className="rounded-lg border border-bg-2 bg-bg-1 p-4 space-y-2">
      <div className="text-fg-0 text-sm font-semibold">Widget footer</div>
      <p className="text-fg-2 text-xs">
        A small attribution line under every rendered widget. Free shows
        “Powered by MCPBuild”; Pro can customize or remove it.
      </p>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={DEFAULT_FOOTER}
        maxLength={120}
        disabled={hidden}
        className="w-full bg-bg-2 border border-bg-2 rounded-md px-3 py-2 text-fg-0 text-xs disabled:opacity-50"
      />
      <label className="flex items-center gap-2 text-fg-1 text-xs">
        <input
          type="checkbox"
          checked={hidden}
          onChange={(e) => setHidden(e.target.checked)}
        />
        Hide footer
      </label>
      <div className="text-fg-2 text-[11px]">
        Preview: <span className="text-fg-1">{preview}</span>
      </div>
      <ProUpgradeNudge error={save.error} />
      <button
        disabled={save.isPending}
        onClick={() => save.mutate()}
        className="px-3 py-1.5 rounded-md bg-accent text-white text-xs hover:opacity-90 disabled:opacity-50"
      >
        {save.isPending ? "Saving…" : "Save footer"}
      </button>
    </div>
  );
}

function DataToolView({
  detail,
  loading,
}: {
  detail: AppToolDetail | undefined;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex-1 rounded-md bg-bg-1 border border-bg-2 grid place-items-center text-fg-2 text-sm">
        Loading…
      </div>
    );
  }
  const schema = detail?.paramsJsonSchema;
  const data = detail?.data;
  return (
    <div className="flex-1 overflow-auto rounded-md bg-bg-1 border border-bg-2 p-4 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="px-1.5 py-0.5 rounded bg-accent/15 text-accent text-[10px] uppercase tracking-wide">
          Data tool
        </span>
        <span className="text-fg-2 text-xs">
          Returns JSON — no widget. A widget's button can call it via{" "}
          <code className="font-mono">callTool()</code>.
        </span>
      </div>
      {detail?.description && <p className="text-fg-1 text-sm">{detail.description}</p>}
      <div>
        <div className="text-xs uppercase tracking-wider text-fg-2 mb-1">Input schema</div>
        <pre className="text-xs font-mono bg-bg-2 rounded p-3 overflow-auto text-fg-1">
          {schema ? JSON.stringify(schema, null, 2) : "{ }"}
        </pre>
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-fg-2 mb-1">
          Returned data <span className="text-fg-2/60">(merged with call args)</span>
        </div>
        <pre className="text-xs font-mono bg-bg-2 rounded p-3 overflow-auto text-fg-1">
          {data != null ? JSON.stringify(data, null, 2) : "null"}
        </pre>
      </div>
    </div>
  );
}

function InboxGallery({
  appName,
  appIcon,
  tools,
  hiddenCount,
  loading,
  zipping,
  onDownloadAll,
  onPick,
}: {
  appName: string;
  appIcon: string;
  tools: AppTool[];
  hiddenCount: number;
  loading: boolean;
  zipping: boolean;
  onDownloadAll: () => void;
  onPick: (toolSlug: string) => void;
}) {
  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-2xl mb-1">{appIcon}</div>
          <h1 className="text-2xl font-semibold">{appName}</h1>
          <p className="text-fg-2 text-sm max-w-xl mt-1">
            Ephemeral widgets from <code className="font-mono">dynamic_widget</code>.
            Each entry expires 24h after creation — click into one and use{" "}
            <strong>Save to project</strong> to keep it.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {tools.length > 0 && (
            <button
              onClick={onDownloadAll}
              disabled={zipping}
              className="px-2 py-1 rounded text-xs border border-bg-2 text-fg-1 hover:text-fg-0 disabled:opacity-50"
            >
              {zipping ? "Zipping…" : "⬇ Download all (.zip)"}
            </button>
          )}
          <div className="text-fg-2 text-xs font-mono text-right">
            <div>{tools.length} widget{tools.length === 1 ? "" : "s"}</div>
            {hiddenCount > 0 && (
              <div className="text-fg-2/60">
                {hiddenCount} broken hidden
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && <div className="text-fg-1 text-sm">Loading…</div>}

      {!loading && tools.length === 0 && (
        <div className="rounded-md border border-bg-2 bg-bg-1 p-6 text-fg-1 text-sm">
          No widgets yet. Use <code className="font-mono">dynamic_widget</code> in
          Claude or ChatGPT — your renders land here automatically.
        </div>
      )}

      {tools.length > 0 && (
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
          {tools.map((t) => (
            <GalleryCard key={t.toolSlug} tool={t} onClick={() => onPick(t.toolSlug)} />
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryCard({ tool, onClick }: { tool: AppTool; onClick: () => void }) {
  // Scale-trick: render the iframe at 200% of the card and CSS-scale to 50%
  // so the widget thinks it's rendering at full size but visually fits the
  // card. pointer-events: none lets clicks bubble to the card itself.
  const time = tool.updatedAt ? new Date(tool.updatedAt).toLocaleString() : null;
  const label = tool.description ?? tool.toolSlug;
  return (
    <button
      onClick={onClick}
      className="group relative rounded-lg overflow-hidden border border-bg-2 bg-white hover:border-accent transition-colors text-left aspect-[4/3]"
      title={tool.description ?? tool.toolSlug}
    >
      {tool.htmlUrl ? (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <iframe
            src={tool.htmlUrl}
            title={tool.toolSlug}
            sandbox="allow-scripts allow-same-origin"
            className="origin-top-left"
            style={{
              width: "200%",
              height: "200%",
              transform: "scale(0.5)",
              border: 0,
            }}
          />
        </div>
      ) : (
        <div className="absolute inset-0 grid place-items-center text-fg-2 text-sm bg-bg-1">
          (no preview)
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 px-3 py-2 bg-gradient-to-t from-black/85 via-black/60 to-transparent text-white">
        <div className="text-sm font-medium leading-tight truncate">{label}</div>
        <div className="mt-0.5 flex items-center gap-2 text-[10px] font-mono opacity-80">
          <span className="truncate">{tool.toolSlug}</span>
          {time && <span className="opacity-70">· {time}</span>}
        </div>
      </div>
    </button>
  );
}

function SharePanel({
  slug,
  isPublic,
  shareToken,
}: {
  slug: string;
  isPublic: boolean;
  shareToken: string | null;
}) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [mcpCopied, setMcpCopied] = useState(false);
  const publicUrl = shareToken ? `${window.location.origin}/p/${shareToken}` : "";
  const publicMcpUrl = shareToken ? `${window.location.origin}/mcp/public/${shareToken}` : "";
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["app", slug] });
    qc.invalidateQueries({ queryKey: ["apps"] });
  };
  const publish = useMutation({ mutationFn: () => publishProject(slug), onSuccess: refresh });
  const unpublish = useMutation({ mutationFn: () => unpublishProject(slug), onSuccess: refresh });

  return (
    <div className="rounded-lg border border-bg-2 bg-bg-1 p-4 space-y-2">
      <div className="flex items-center gap-2">
        <div className="text-fg-0 text-sm font-semibold mr-auto">Public link</div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-fg-2">
          {isPublic ? "shared" : "private"}
        </span>
      </div>
      {isPublic && publicUrl ? (
        <>
          <p className="text-fg-2 text-xs">Anyone with this link can view this project's widgets (read-only).</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-bg-2 border border-bg-2 rounded-md px-3 py-2 text-fg-0 text-xs font-mono break-all">
              {publicUrl}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(publicUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              className="px-3 py-2 rounded-md bg-accent text-white text-xs hover:opacity-90 shrink-0"
            >
              {copied ? "copied" : "copy"}
            </button>
          </div>
          <div className="pt-1">
            <div className="text-fg-2 text-[11px] mb-1">
              Public MCP endpoint — anyone can connect this in Claude (no sign-in)
              to run this project's widgets:
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-bg-2 border border-bg-2 rounded-md px-3 py-2 text-fg-0 text-xs font-mono break-all">
                {publicMcpUrl}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(publicMcpUrl);
                  setMcpCopied(true);
                  setTimeout(() => setMcpCopied(false), 1500);
                }}
                className="px-3 py-2 rounded-md bg-bg-2 text-fg-1 text-xs hover:text-fg-0 shrink-0"
              >
                {mcpCopied ? "copied" : "copy"}
              </button>
            </div>
            <div className="mt-2 text-fg-2 text-[11px] mb-1">
              Browser extensions for this project — pick a mode:
            </div>
            <div className="flex flex-col gap-1.5">
              <a
                href={`${window.location.origin}/api/public/${shareToken}/extension.zip`}
                className="block px-3 py-2 rounded-md bg-bg-2 text-fg-1 text-xs hover:text-fg-0"
              >
                ⬇ <strong>Agent bridge</strong> (.zip)
                <span className="block text-fg-2 text-[10px] mt-0.5">
                  Exposes the tools to Chrome's in-browser AI agent via WebMCP
                  (experimental — needs Chrome's WebMCP flag).
                </span>
              </a>
              <a
                href={`${window.location.origin}/api/public/${shareToken}/extension.zip?mode=injector`}
                className="block px-3 py-2 rounded-md bg-bg-2 text-fg-1 text-xs hover:text-fg-0"
              >
                ⬇ <strong>SERP injector</strong> (.zip)
                <span className="block text-fg-2 text-[10px] mt-0.5">
                  Injects this project's widgets/tools onto Google · Bing ·
                  DuckDuckGo results (no flag; edit <code className="font-mono">config.js</code> to target specific results).
                </span>
              </a>
            </div>
          </div>
          <button
            disabled={unpublish.isPending}
            onClick={() => unpublish.mutate()}
            className="text-xs text-fg-2 hover:text-red-400 disabled:opacity-50"
          >
            {unpublish.isPending ? "Revoking…" : "Unpublish (revoke both links)"}
          </button>
        </>
      ) : (
        <>
          <p className="text-fg-2 text-xs">
            Publish this project to get two shareable, no-sign-in links: a
            read-only <strong>gallery</strong> of its widgets, and a public
            <strong> MCP endpoint</strong> anyone can connect in Claude to run
            them as tools. Revoke anytime.
          </p>
          <ProUpgradeNudge error={publish.error} />
          <button
            disabled={publish.isPending}
            onClick={() => publish.mutate()}
            className="px-3 py-1.5 rounded-md bg-accent text-white text-xs hover:opacity-90 disabled:opacity-50"
          >
            {publish.isPending ? "Publishing…" : "Publish publicly (gallery + MCP)"}
          </button>
        </>
      )}
    </div>
  );
}

// Team-sharing entry point on the project page. Counts members and links to
// the full Members page. Hidden on the Inbox (already filtered out earlier).
function TeamPanel({ slug }: { slug: string }) {
  const members = useQuery({
    queryKey: ["members", slug],
    queryFn: () => listMembers(slug),
    retry: false,
  });
  const count = members.data?.members.length ?? 0;
  const pending = (members.data?.members ?? []).filter((m) => !m.accepted).length;
  return (
    <details className="rounded-lg border border-bg-2 bg-bg-1 p-4">
      <summary className="cursor-pointer text-sm font-medium flex items-center gap-2">
        <span>Team & sharing</span>
        {count > 0 && (
          <span className="text-[10px] font-mono text-fg-2 border border-bg-2 rounded-full px-2 py-[1px]">
            {count} {count === 1 ? "member" : "members"}
            {pending > 0 ? ` · ${pending} pending` : ""}
          </span>
        )}
      </summary>
      <div className="mt-3 space-y-2">
        <p className="text-fg-2 text-xs">
          Invite people by email to view (read) or edit (write) this project.
          You can also share just one widget. Invitees see the project at <code className="font-mono">/shared</code> after signing in.
          <span className="block mt-1 text-fg-3">Pro feature.</span>
        </p>
        <Link
          to={`/projects/${slug}/members`}
          className="inline-block px-3 py-1.5 rounded-md bg-accent text-white text-xs hover:opacity-90"
        >
          Manage members →
        </Link>
      </div>
    </details>
  );
}

function LiveDataPanel({ slug, allowlist }: { slug: string; allowlist: string[] }) {
  const qc = useQueryClient();
  const [domains, setDomains] = useState(allowlist.join("\n"));
  const [secretName, setSecretName] = useState("");
  const [secretValue, setSecretValue] = useState("");

  const secrets = useQuery({
    queryKey: ["secrets", slug],
    queryFn: () => listSecrets(slug),
    retry: false,
  });
  const saveAllow = useMutation({
    mutationFn: () =>
      setFetchAllowlist(
        slug,
        domains.split("\n").map((d) => d.trim()).filter(Boolean),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["app", slug] }),
  });
  const addSecret = useMutation({
    mutationFn: () => setSecret(slug, secretName.trim(), secretValue),
    onSuccess: () => {
      setSecretName("");
      setSecretValue("");
      qc.invalidateQueries({ queryKey: ["secrets", slug] });
    },
  });
  const removeSecret = useMutation({
    mutationFn: (name: string) => deleteSecret(slug, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["secrets", slug] }),
  });

  return (
    <details className="rounded-lg border border-bg-2 bg-bg-1 p-4">
      <summary className="cursor-pointer text-fg-0 text-sm font-semibold select-none">
        Live data{" "}
        <span className="text-fg-2 font-normal text-xs">— let widgets fetch APIs</span>
      </summary>
      <div className="mt-3 space-y-4">
        <div className="space-y-1.5">
          <div className="text-fg-2 text-xs uppercase tracking-wider">Allowed domains</div>
          <p className="text-fg-2 text-[11px]">
            One per line. Widgets in this project can only fetch from these (https
            only). e.g. <code className="font-mono">api.github.com</code> or{" "}
            <code className="font-mono">*.example.com</code>.
          </p>
          <textarea
            value={domains}
            onChange={(e) => setDomains(e.target.value)}
            rows={3}
            placeholder="api.example.com"
            className="w-full rounded-md bg-bg-2 border border-bg-2 px-2 py-1.5 text-fg-0 text-xs font-mono"
          />
          <button
            disabled={saveAllow.isPending}
            onClick={() => saveAllow.mutate()}
            className="px-2 py-1 rounded text-xs bg-accent text-white hover:opacity-90 disabled:opacity-50"
          >
            {saveAllow.isPending ? "Saving…" : "Save domains"}
          </button>
          <ProUpgradeNudge error={saveAllow.error} />
        </div>

        <div className="space-y-1.5">
          <div className="text-fg-2 text-xs uppercase tracking-wider">Secrets</div>
          <p className="text-fg-2 text-[11px]">
            Encrypted API keys. Reference in a widget's fetch as{" "}
            <code className="font-mono">{"{{secret:NAME}}"}</code> — injected
            server-side, never exposed. Values are write-only.
          </p>
          {secrets.data && secrets.data.secrets.length > 0 && (
            <ul className="space-y-1">
              {secrets.data.secrets.map((s) => (
                <li key={s.name} className="flex items-center gap-2 text-xs">
                  <code className="font-mono text-fg-1 flex-1 truncate">{s.name}</code>
                  <button
                    disabled={removeSecret.isPending}
                    onClick={() => removeSecret.mutate(s.name)}
                    className="text-fg-2 hover:text-red-400 disabled:opacity-50"
                  >
                    remove
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-1.5">
            <input
              value={secretName}
              onChange={(e) => setSecretName(e.target.value)}
              placeholder="NAME"
              className="w-1/3 rounded-md bg-bg-2 border border-bg-2 px-2 py-1.5 text-fg-0 text-xs font-mono"
            />
            <input
              value={secretValue}
              onChange={(e) => setSecretValue(e.target.value)}
              placeholder="value"
              type="password"
              className="flex-1 rounded-md bg-bg-2 border border-bg-2 px-2 py-1.5 text-fg-0 text-xs font-mono"
            />
            <button
              disabled={addSecret.isPending || !secretName.trim() || !secretValue}
              onClick={() => addSecret.mutate()}
              className="px-2 py-1 rounded text-xs bg-bg-2 text-fg-1 hover:text-fg-0 disabled:opacity-50"
            >
              {addSecret.isPending ? "…" : "add"}
            </button>
          </div>
          {isProRequiredError(addSecret.error) ? (
            <ProUpgradeNudge error={addSecret.error} />
          ) : (
            addSecret.error && (
              <div className="text-red-400 text-[11px] font-mono">
                {addSecret.error instanceof Error ? addSecret.error.message : "failed"}
              </div>
            )
          )}
        </div>
      </div>
    </details>
  );
}

function TriggerPhrasesPane({
  appSlug,
  toolSlug,
  initialPhrases,
  shareId,
  onSaved,
}: {
  appSlug: string;
  toolSlug: string;
  initialPhrases: string[];
  shareId?: string | null;
  onSaved: () => void;
}) {
  const [text, setText] = useState<string>(initialPhrases.join("\n"));
  const [lastSavedKey, setLastSavedKey] = useState<string>(toolSlug);
  // Reset the textarea when the user picks a different tool.
  if (lastSavedKey !== toolSlug) {
    setLastSavedKey(toolSlug);
    setText(initialPhrases.join("\n"));
  }

  const save = useMutation({
    mutationFn: (phrases: string[]) =>
      updateAppTool(appSlug, toolSlug, { triggerPhrases: phrases }, shareId),
    onSuccess: () => onSaved(),
  });

  const phrases = text
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .slice(0, 10);
  const dirty =
    JSON.stringify(phrases) !== JSON.stringify(initialPhrases);
  const sample = phrases[0] || initialPhrases[0] || "Cancel order A-100";

  return (
    <div className="flex-1 min-h-0 rounded-md bg-bg-1 border border-bg-2 p-4 flex flex-col gap-4 overflow-auto">
      <div>
        <div className="text-fg-1 text-sm font-medium mb-1">
          Demo prompts for <code className="font-mono text-accent">{toolSlug}</code>
        </div>
        <div className="text-fg-3 text-xs leading-relaxed max-w-2xl">
          Short, natural sentences a user would actually <em>say</em> to a chat
          client to trigger this tool — not the tool name. Saved phrases are
          appended to the tool's MCP description so models like Claude and
          ChatGPT pick it on natural phrasing, and they appear as one-click
          "Try this prompt" pills below for quick demos. 1-10 phrases, one per
          line, ≤240 chars each.
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        spellCheck
        placeholder={`Cancel order A-100\nRefund the customer named Alice\nWhich orders are still open?`}
        className="w-full rounded-md bg-bg-2 border border-bg-2 px-3 py-2 text-sm text-fg-1 font-sans focus:outline-none focus:border-accent"
        style={{ resize: "vertical", minHeight: 140 }}
      />

      <div className="flex items-center gap-2 flex-wrap">
        <button
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate(phrases)}
          className="px-3 py-1.5 rounded text-sm bg-accent text-white hover:opacity-90 disabled:opacity-40"
        >
          {save.isPending ? "Saving…" : dirty ? "Save phrases" : "Saved"}
        </button>
        <button
          disabled={!dirty || save.isPending}
          onClick={() => setText(initialPhrases.join("\n"))}
          className="px-3 py-1.5 rounded text-sm text-fg-2 hover:text-fg-0 hover:bg-bg-2 disabled:opacity-40"
        >
          Revert
        </button>
        <span className="text-fg-3 text-xs ml-auto">
          {phrases.length}/10 phrases
        </span>
      </div>

      {save.isError && (
        <div className="text-xs text-red-400">
          {save.error instanceof Error ? save.error.message : "Failed to save."}
        </div>
      )}

      {phrases.length > 0 && (
        <div className="border-t border-bg-2 pt-4">
          <div className="text-fg-2 text-xs uppercase tracking-wider mb-2">
            Try this prompt
          </div>
          <div className="flex flex-wrap gap-2">
            {phrases.map((p, i) => (
              <button
                key={`${p}-${i}`}
                onClick={() => {
                  navigator.clipboard.writeText(p);
                }}
                title="Copy to clipboard"
                className="px-3 py-1.5 rounded-full text-xs bg-bg-2 border border-bg-2 text-fg-1 hover:text-fg-0 hover:border-accent-line"
              >
                “{p}”
              </button>
            ))}
          </div>
          <div className="text-fg-4 text-[11px] mt-3 font-mono">
            Sample MCP description tail: <br />
            <span className="text-fg-3">
              Users typically say things like: "- {sample}"
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Renders a project tool's widget preview iframe, plus a postMessage host
// that the bundler's html-wrapper handshakes with so the widget's callTool()
// resolves against this project's tools via the REST API. Without it, widgets
// that fetch via callTool render "callTool unavailable outside host" on the
// website preview — the bundler wrapper's fallback when no host injects
// window.openai. Chat clients (Claude/ChatGPT) iframe a different shell and
// inject their own host, so this code path is website-only.
function WidgetPreviewIframe({
  appSlug,
  shareId,
  tool,
  toolData,
}: {
  appSlug: string;
  shareId: string | null;
  tool: AppTool;
  toolData: unknown;
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Only install the preview host once toolData has actually loaded. Before
  // that we'd post mb:preview-host-init with `data: {}`, which the wrapper's
  // installPreviewHost would lock in (subsequent installs are no-ops once
  // window.openai is set). The bundle's mount.tsx bakes the saved data into
  // globalThis.__mm_widget_output, so as long as we DON'T install an empty
  // host, the widget falls back to the baked data and renders correctly. If
  // toolData arrives within the wrapper's 200ms grace window, we install with
  // real data and callTool works against the live REST API. Either way no
  // empty-data window.
  const hasToolData = toolData !== undefined;

  useEffect(() => {
    if (!hasToolData) return;
    const iframeEl: HTMLIFrameElement | null = iframeRef.current;
    if (!iframeEl) return;
    const iframe = iframeEl; // narrow once for closure use below
    const initToolOutput = {
      // useToolOutput() reads .data — same shape callProjectTool builds for
      // chat-client renders.
      data: (toolData && typeof toolData === "object" ? toolData : {}) as Record<string, unknown>,
      bundleUrl: tool.bundleUrl,
      htmlUrl: tool.htmlUrl,
      displayMode: tool.displayMode,
      widgetId: tool.toolSlug,
    };

    function post(msg: Record<string, unknown>) {
      try {
        iframe.contentWindow?.postMessage(msg, "*");
      } catch {
        /* iframe gone */
      }
    }

    async function resolveToolCall(name: string, args: Record<string, unknown>) {
      try {
        const detail = await getAppTool(appSlug, name, shareId);
        const saved =
          detail.data && typeof detail.data === "object"
            ? (detail.data as Record<string, unknown>)
            : {};
        // Mirrors callProjectTool's shape for data tools (saved JSON shallow-
        // merged with the call's args). For UI tools we return the saved
        // data + the bundle pointers so a widget that calls another widget
        // doesn't blow up — but the website preview won't iframe nested
        // widgets; the LLM-style use case is data tools.
        return { ...saved, ...args };
      } catch (e) {
        throw e instanceof Error ? e : new Error(String(e));
      }
    }

    function onMessage(ev: MessageEvent) {
      if (ev.source !== iframe.contentWindow) return;
      const data = ev.data;
      if (!data || typeof data.type !== "string") return;
      if (data.type === "mb:widget-ready") {
        post({ type: "mb:preview-host-init", toolOutput: initToolOutput });
        return;
      }
      if (data.type === "mb:preview-tool-call") {
        const callId = data.id;
        const name = typeof data.name === "string" ? data.name : "";
        const args =
          data.args && typeof data.args === "object" ? (data.args as Record<string, unknown>) : {};
        resolveToolCall(name, args)
          .then((result) => post({ type: "mb:preview-tool-result", id: callId, result }))
          .catch((err: Error) =>
            post({ type: "mb:preview-tool-result", id: callId, error: err.message }),
          );
      }
    }

    // The iframe may have already posted mb:widget-ready and timed out by the
    // time this effect runs (toolData arrives a tick or two after the iframe
    // loads). Send the host-init proactively in that case — the wrapper's
    // installPreviewHost is a no-op if window.openai was already set, but if
    // the wrapper already booted with the baked data and no host, this lets a
    // subsequent install land before the user clicks anything callTool-bound.
    post({ type: "mb:preview-host-init", toolOutput: initToolOutput });

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [
    appSlug,
    shareId,
    tool.toolSlug,
    tool.bundleUrl,
    tool.htmlUrl,
    tool.displayMode,
    toolData,
    hasToolData,
  ]);

  return (
    <iframe
      ref={iframeRef}
      key={tool.toolSlug}
      title={tool.toolSlug}
      src={tool.htmlUrl ?? undefined}
      className="flex-1 bg-white"
      style={{ border: 0 }}
      sandbox="allow-scripts allow-same-origin"
    />
  );
}

function VersionsPane({ appSlug, toolSlug }: { appSlug: string; toolSlug: string }) {
  const qc = useQueryClient();
  const versions = useQuery({
    queryKey: ["tool-versions", appSlug, toolSlug],
    queryFn: () => listToolVersions(appSlug, toolSlug),
    enabled: !!appSlug && !!toolSlug,
    retry: false,
  });
  const restore = useMutation({
    mutationFn: (version: number) => restoreToolVersion(appSlug, toolSlug, version),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tool-versions", appSlug, toolSlug] });
      qc.invalidateQueries({ queryKey: ["app-tool", appSlug, toolSlug] });
      qc.invalidateQueries({ queryKey: ["app-tools", appSlug] });
    },
  });

  return (
    <div className="flex-1 min-h-0 rounded-md bg-bg-1 border border-bg-2 overflow-auto">
      <div className="px-3 py-2 border-b border-bg-2 text-xs text-fg-2">
        Each remix (in Claude) saves a new version here. Restore re-points the tool
        at an older version — history is kept.
      </div>
      {versions.isLoading && <div className="p-3 text-fg-2 text-sm">Loading…</div>}
      {versions.error && (
        <div className="p-3 text-red-400 text-xs font-mono">
          {versions.error instanceof Error ? versions.error.message : String(versions.error)}
        </div>
      )}
      {versions.data && (
        <ul className="divide-y divide-bg-2">
          {versions.data.versions.map((v) => {
            const isCurrent = v.version === versions.data!.current;
            return (
              <li key={v.version} className="flex items-center gap-3 px-3 py-2">
                <span className="font-mono text-xs text-fg-0 w-10">v{v.version}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-fg-1 truncate">
                    {v.changeNote ?? <span className="text-fg-2 italic">no note</span>}
                  </div>
                  {v.createdAt && (
                    <div className="text-[10px] text-fg-2 font-mono">
                      {new Date(v.createdAt).toLocaleString()}
                    </div>
                  )}
                </div>
                {isCurrent ? (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-accent">
                    current
                  </span>
                ) : (
                  <button
                    disabled={restore.isPending}
                    onClick={() => restore.mutate(v.version)}
                    className="px-2 py-1 rounded text-xs border border-bg-2 text-fg-1 hover:text-fg-0 disabled:opacity-50"
                  >
                    {restore.isPending ? "…" : "Restore"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function PaneTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 12px",
        borderRadius: 6,
        background: active ? "var(--surface-2)" : "transparent",
        color: active ? "var(--fg-1)" : "var(--fg-3)",
        border: active ? "1px solid var(--line-2)" : "1px solid transparent",
        cursor: "pointer",
        fontFamily: "var(--font-sans)",
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      {label}
    </button>
  );
}

function CodePane({
  text,
  loading,
  filename,
  mime,
  language,
  canFormat = false,
  emptyReason,
}: {
  text: string | null;
  loading: boolean;
  filename: string;
  mime: string;
  language: "tsx" | "json";
  canFormat?: boolean;
  // When `text` is null, why? Differentiates "no tool selected" from "older
  // widget with no captured source" from "data tool doesn't have this pane".
  // CodePane renders the matching empty-state copy.
  emptyReason?: "no-selection" | "data-tool" | "pre-storage" | "loading-tool";
}) {
  const [copied, setCopied] = useState(false);
  const [body, setBody] = useState<string | null>(null);
  const [formatErr, setFormatErr] = useState<string | null>(null);
  const [formatting, setFormatting] = useState(false);

  const display = body ?? text;

  if (loading) {
    return (
      <div className="flex-1 rounded-md bg-bg-1 border border-bg-2 grid place-items-center text-fg-2 text-sm">
        Loading…
      </div>
    );
  }
  if (!display) {
    const kind =
      language === "json"
        ? { label: "data", what: "structured data" }
        : { label: "source", what: "React source" };
    let title = "";
    let detail = "";
    if (emptyReason === "no-selection") {
      title = `Pick a tool on the left to view its ${kind.label}.`;
      detail = "";
    } else if (emptyReason === "data-tool") {
      title = `Data tools don't have ${kind.what}.`;
      detail = `They return JSON directly to the caller — see the “Tool” pane for the schema and returned data.`;
    } else if (emptyReason === "pre-storage") {
      title = `No stored ${kind.label} for this widget.`;
      detail = `Widgets created before 2026-05-21 don't have ${kind.what} captured in S3. Re-create the widget via dynamic_widget to populate it.`;
    } else if (emptyReason === "loading-tool") {
      title = `Loading ${kind.label}…`;
      detail = "";
    } else {
      title = `No ${kind.label} stored for this widget.`;
      detail = "";
    }
    return (
      <div className="flex-1 rounded-md bg-bg-1 border border-bg-2 grid place-items-center px-6">
        <div className="text-center max-w-md">
          <div className="text-fg-1 text-sm font-medium">{title}</div>
          {detail && (
            <div className="text-fg-3 text-xs mt-2 leading-relaxed">{detail}</div>
          )}
        </div>
      </div>
    );
  }

  const download = () => {
    const blob = new Blob([display], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex-1 min-h-0 rounded-md bg-bg-1 border border-bg-2 flex flex-col">
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-bg-2 text-xs">
        <code className="font-mono text-fg-2 mr-auto">{filename}</code>
        {canFormat && (
          <button
            disabled={formatting}
            onClick={async () => {
              setFormatErr(null);
              setFormatting(true);
              try {
                const formatted = await formatTsx(display);
                setBody(formatted);
              } catch (e) {
                setFormatErr(e instanceof Error ? e.message : String(e));
              } finally {
                setFormatting(false);
              }
            }}
            className="px-2 py-0.5 rounded text-fg-1 hover:text-fg-0 hover:bg-bg-2 disabled:opacity-50"
          >
            {formatting ? "formatting…" : "format"}
          </button>
        )}
        <button
          onClick={() => {
            navigator.clipboard.writeText(display);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="px-2 py-0.5 rounded text-fg-1 hover:text-fg-0 hover:bg-bg-2"
        >
          {copied ? "copied" : "copy"}
        </button>
        <button
          onClick={download}
          className="px-2 py-0.5 rounded text-fg-1 hover:text-fg-0 hover:bg-bg-2"
        >
          download
        </button>
      </div>
      <Highlight code={display} language={language} theme={themes.nightOwl}>
        {({ tokens, getLineProps, getTokenProps, style }) => (
          <pre
            className="flex-1 overflow-auto px-3 py-2 text-xs font-mono whitespace-pre leading-relaxed"
            style={{ ...style, background: "transparent", margin: 0 }}
          >
            {tokens.map((line, i) => {
              const { key: _lineKey, ...lineProps } = getLineProps({ line });
              return (
                <div key={i} {...lineProps}>
                  <span
                    className="inline-block w-8 mr-3 text-fg-2 select-none text-right"
                    aria-hidden
                  >
                    {i + 1}
                  </span>
                  {line.map((tok, j) => {
                    const { key: _tokKey, ...tokProps } = getTokenProps({ token: tok });
                    return <span key={j} {...tokProps} />;
                  })}
                </div>
              );
            })}
          </pre>
        )}
      </Highlight>
      {formatErr && (
        <div className="border-t border-bg-2 px-3 py-1.5 text-xs text-red-400 font-mono">
          format failed: {formatErr}
        </div>
      )}
    </div>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function SaveToAppModal({
  sourceAppSlug,
  sourceToolSlug,
  defaultDescription,
  onClose,
  onSaved,
}: {
  sourceAppSlug: string;
  sourceToolSlug: string;
  defaultDescription: string;
  onClose: () => void;
  onSaved: (targetAppSlug: string, targetToolSlug: string) => void;
}) {
  const apps = useQuery({ queryKey: ["apps"], queryFn: listApps, retry: false });
  const candidates = useMemo(
    () => (apps.data ?? []).filter((a: App) => a.slug !== "__inbox"),
    [apps.data],
  );
  const [targetAppSlug, setTargetAppSlug] = useState<string>("__new__");
  const [newAppName, setNewAppName] = useState("");
  const [name, setName] = useState(defaultDescription || "");
  // Keep slug + name in sync until the user manually edits the slug.
  const [slugTouched, setSlugTouched] = useState(false);
  const [toolSlug, setToolSlug] = useState(
    !sourceToolSlug.startsWith("widget_") ? sourceToolSlug : "",
  );
  const derivedSlug = slugify(name) || (!sourceToolSlug.startsWith("widget_") ? sourceToolSlug : "");
  const effectiveSlug = slugTouched ? toolSlug : derivedSlug;

  const mutation = useMutation({
    mutationFn: () =>
      saveToolToApp(sourceAppSlug, sourceToolSlug, {
        targetAppSlug: targetAppSlug === "__new__" ? undefined : targetAppSlug,
        targetAppName: targetAppSlug === "__new__" ? newAppName : undefined,
        targetToolSlug: effectiveSlug || undefined,
        description: name || undefined,
      }),
    onSuccess: (r) => onSaved(r.appSlug, r.toolSlug),
  });

  const canSubmit =
    name.trim().length > 0 &&
    effectiveSlug.length > 0 &&
    (targetAppSlug !== "__new__" || newAppName.trim().length > 0) &&
    !mutation.isPending;

  return (
    <div
      className="fixed inset-0 bg-black/60 grid place-items-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-bg-1 border border-bg-2 rounded-lg p-6 w-[480px] max-w-[90vw] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-fg-0 font-semibold">Save widget to project</h2>
        <p className="text-xs text-fg-2">
          Re-bundles the widget under a persistent project — the Inbox copy still expires in 24h
          but the saved copy sticks around.
        </p>

        <div className="space-y-1">
          <label className="text-xs text-fg-2">Widget name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Quarterly Revenue Chart"
            autoFocus
            className="w-full rounded-md bg-bg-2 border border-bg-2 px-3 py-2 text-fg-0 text-sm"
          />
        </div>

        <div className="space-y-1">
          <label className="text-xs text-fg-2 flex items-center justify-between">
            <span>Tool slug</span>
            {slugTouched && derivedSlug && derivedSlug !== toolSlug && (
              <button
                onClick={() => {
                  setSlugTouched(false);
                  setToolSlug("");
                }}
                className="text-fg-2 hover:text-fg-0 text-[10px]"
              >
                reset to auto
              </button>
            )}
          </label>
          <input
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setToolSlug(slugify(e.target.value));
            }}
            placeholder="quarterly-revenue-chart"
            className="w-full rounded-md bg-bg-2 border border-bg-2 px-3 py-2 text-fg-0 text-sm font-mono"
          />
          <div className="text-[10px] text-fg-2">
            URL: <code className="font-mono">ui://{targetAppSlug === "__new__" ? slugify(newAppName) || "new-app" : targetAppSlug}/{effectiveSlug || "…"}</code>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-xs text-fg-2">Target project</label>
          <select
            value={targetAppSlug}
            onChange={(e) => setTargetAppSlug(e.target.value)}
            className="w-full rounded-md bg-bg-2 border border-bg-2 px-3 py-2 text-fg-0 text-sm"
          >
            <option value="__new__">+ Create new project…</option>
            {candidates.map((a: App) => (
              <option key={a.slug} value={a.slug}>
                {a.icon ?? "🧩"} {a.name}
              </option>
            ))}
          </select>
        </div>

        {targetAppSlug === "__new__" && (
          <div className="space-y-1">
            <label className="text-xs text-fg-2">New project name</label>
            <input
              value={newAppName}
              onChange={(e) => setNewAppName(e.target.value)}
              placeholder="Sales Dashboard"
              className="w-full rounded-md bg-bg-2 border border-bg-2 px-3 py-2 text-fg-0 text-sm"
            />
          </div>
        )}

        {mutation.error && (
          <div className="text-xs text-red-400 font-mono">
            {mutation.error instanceof Error
              ? mutation.error.message
              : String(mutation.error)}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md text-fg-1 text-sm hover:text-fg-0"
          >
            Cancel
          </button>
          <button
            disabled={!canSubmit}
            onClick={() => mutation.mutate()}
            className="px-3 py-1.5 rounded-md bg-accent text-white text-sm disabled:opacity-50"
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
